import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import threatProfiles from '../../../shared/data/threatProfiles.json'
import technologyScopingData from '../../../shared/data/technologyScoping.json'
import { deriveMarketSegment } from '../../../shared/utils/marketSegment'
import { getOrgCharacteristics, getFrameworkOverlays, buildFullRegulatoryContext } from '../../../shared/utils/orgCharacteristics'
import { recommendProfile, isActiveProfile } from '../../../shared/utils/profileRecommendation'

/**
 * Detect technology ecosystem from technology scoping responses
 */
function detectTechnologyEcosystem(technologyScoping) {
  if (!technologyScoping) return null

  const platformHints = []
  const hintsByQuestion = {} // Track platformHint for each question ID

  // Check each technology scoping response for platform hints
  technologyScopingData.sections.forEach(section => {
    section.questions.forEach(question => {
      const response = technologyScoping[question.id]
      if (response?.value) {
        const matchedOption = question.options.find(opt => opt.label === response.value)
        if (matchedOption?.platformHint) {
          platformHints.push(matchedOption.platformHint)
          hintsByQuestion[question.id] = matchedOption.platformHint
        }
      }
    })
  })

  // Determine primary ecosystem
  const microsoftCount = platformHints.filter(h => h === 'microsoft' || h === 'azure').length
  const googleCount = platformHints.filter(h => h === 'google' || h === 'gcp').length
  const onPremCount = platformHints.filter(h => h === 'on-prem').length
  const cloudNativeCount = platformHints.filter(h => ['azure', 'aws', 'gcp', 'saas-only'].includes(h)).length

  let primaryEcosystem = 'mixed'
  if (microsoftCount >= 2) primaryEcosystem = 'microsoft'
  else if (googleCount >= 2) primaryEcosystem = 'google'
  else if (onPremCount >= 2) primaryEcosystem = 'on-premises'
  else if (cloudNativeCount >= 3) primaryEcosystem = 'cloud-native'

  // Identify security gaps - check specific question's platformHint, not global array
  const hasNoBackup = hintsByQuestion['ts-5'] === 'none'
  const hasNoEndpoint = hintsByQuestion['ts-4'] === 'none'
  const hasNoIdentity = hintsByQuestion['ts-2'] === 'none'
  const hasTraditionalAV = hintsByQuestion['ts-4'] === 'traditional-av'
  const hasManualBackup = hintsByQuestion['ts-5'] === 'manual'

  return {
    primary: primaryEcosystem,
    platformHints,
    gaps: {
      noBackup: hasNoBackup,
      noEndpoint: hasNoEndpoint,
      noIdentity: hasNoIdentity,
      traditionalAV: hasTraditionalAV,
      manualBackup: hasManualBackup
    },
    riskElevation: (hasNoBackup || hasNoEndpoint || hasNoIdentity) ? 'critical' :
                   (hasTraditionalAV || hasManualBackup) ? 'elevated' : 'normal'
  }
}

function ThreatContextStep({ responses, onSave, client, showOnlyRecommendation = false, hideRecommendation = false, hideThreatLandscape = false, hideThreatPreview = false }) {
  const [selectedProfile, setSelectedProfile] = useState(null)
  const [showProfileOverride, setShowProfileOverride] = useState(false)
  const [isManualOverride, setIsManualOverride] = useState(false)

  // Track initialization to prevent auto-save during initial load
  const isInitializing = useRef(true)
  const saveTimeoutRef = useRef(null)

  // Detect technology ecosystem from responses
  const techEcosystem = useMemo(() =>
    detectTechnologyEcosystem(responses?.technologyScoping),
    [responses?.technologyScoping]
  )

  // Derive market segment from business scoping
  const marketSegment = useMemo(() =>
    deriveMarketSegment(responses?.businessScoping),
    [responses?.businessScoping]
  )

  // Get organizational characteristics
  const orgCharacteristics = useMemo(() =>
    getOrgCharacteristics(responses?.businessScoping),
    [responses?.businessScoping]
  )

  // Get framework overlays from Business Scoping rf-1 selections (HIPAA, PCI DSS, etc.)
  const frameworkOverlays = useMemo(() =>
    getFrameworkOverlays(responses?.businessScoping),
    [responses?.businessScoping]
  )

  // Get profile recommendation from the multi-signal scoring engine
  const profileRecommendation = useMemo(() =>
    recommendProfile({
      client: client || {},
      businessScoping: responses?.businessScoping || {}
    }),
    [client, responses?.businessScoping]
  )

  // Resolve active sub-industry overlay from the selected profile
  const activeSubIndustry = useMemo(() => {
    if (!selectedProfile?.subIndustries) return null

    const subKey = profileRecommendation?.subIndustryKey
      || client?.industry_sub_category
      || client?.industrySubCategory
      || null

    if (!subKey) return null

    return selectedProfile.subIndustries.find(si => si.key === subKey) || null
  }, [selectedProfile, profileRecommendation?.subIndustryKey, client?.industry_sub_category, client?.industrySubCategory])

  // Merge target assets with sub-industry additions
  const displayTargetAssets = useMemo(() => {
    const baseAssets = selectedProfile?.targetAssets || []
    const additions = activeSubIndustry?.targetAssetAdditions || []
    if (!additions.length) return baseAssets

    const existingIds = new Set(baseAssets.map(a => a.id))
    const merged = [...baseAssets]
    additions.forEach(addition => {
      if (!existingIds.has(addition.id)) {
        merged.push({
          ...addition,
          prevalence: addition.criticality || 0.8,
          defaultWeight: addition.criticality || 0.8,
          isSubIndustryAddition: true
        })
      }
    })
    return merged
  }, [selectedProfile?.targetAssets, activeSubIndustry?.targetAssetAdditions])

  // Auto-select profile based on recommendation engine (unless manually overridden)
  useEffect(() => {
    // Skip if user has manually overridden
    if (isManualOverride) return

    // Use recommendation from the scoring engine
    if (profileRecommendation?.profileId) {
      // Find the full profile from threatProfiles.json
      let profile = threatProfiles.profiles.find(p => p.verticalKey === profileRecommendation.profileId)

      // If recommended profile not found in full profiles (e.g., TP-NP not yet created),
      // fall back to TP-DEFAULT
      if (!profile) {
        profile = threatProfiles.profiles.find(p => p.verticalKey === 'TP-DEFAULT')
      }

      if (profile) {
        setSelectedProfile(profile)
      }
    }
  }, [profileRecommendation, isManualOverride])

  // Load saved context if available
  useEffect(() => {
    const savedContext = responses?.threatContext
    if (savedContext) {
      if (savedContext.profileKey) {
        const profile = threatProfiles.profiles.find(p => p.verticalKey === savedContext.profileKey)
        if (profile) setSelectedProfile(profile)
      }
    }
  }, [responses?.threatContext])

  // Calculate summary scores from profile defaults
  const summaryScores = useMemo(() => {
    if (!selectedProfile) return null

    const calcAverage = (items) => {
      let total = 0
      let count = 0
      items.forEach(item => {
        const weight = item.defaultWeight ?? 1
        const score = item.likelihood ?? item.prevalence ?? item.criticality ?? 0
        total += score * weight
        count++
      })
      return count > 0 ? Math.round((total / count) * 100) : 0
    }

    return {
      threatActorScore: calcAverage(selectedProfile.threatActors),
      attackVectorScore: calcAverage(selectedProfile.attackVectors),
      targetAssetScore: calcAverage(selectedProfile.targetAssets)
    }
  }, [selectedProfile])

  const handleProfileChange = (verticalKey) => {
    const profile = threatProfiles.profiles.find(p => p.verticalKey === verticalKey)
    setSelectedProfile(profile)
    setShowProfileOverride(false)
    // Mark as manual override if different from recommendation
    if (verticalKey !== profileRecommendation?.profileId) {
      setIsManualOverride(true)
    }
  }

  const handleResetToRecommendation = () => {
    setIsManualOverride(false)
    setShowProfileOverride(false)
    // Profile will be set by the useEffect watching profileRecommendation
  }

  // Get active profiles for the override selector
  const activeProfiles = useMemo(() => {
    // Filter to only show active profiles that exist in threatProfiles.json
    return threatProfiles.profiles.filter(p => isActiveProfile(p.verticalKey))
  }, [])

  const handleSave = useCallback(() => {
    if (!selectedProfile) return

    // Build enhanced regulatory context with org characteristic + framework overlays
    const baseRegulatoryContext = activeSubIndustry?.regulatoryContext || selectedProfile.regulatoryContext
    const enhancedRegulatoryContext = buildFullRegulatoryContext(
      baseRegulatoryContext,
      orgCharacteristics,
      frameworkOverlays
    )

    const contextData = {
      profileKey: selectedProfile.verticalKey,
      profileName: selectedProfile.verticalName,
      subIndustryKey: activeSubIndustry?.key || null,
      subIndustryDisplayName: activeSubIndustry?.displayName || null,
      regulatoryContext: enhancedRegulatoryContext,
      summaryScores,
      // Include technology ecosystem context
      technologyEcosystem: techEcosystem ? {
        primary: techEcosystem.primary,
        riskElevation: techEcosystem.riskElevation,
        gaps: techEcosystem.gaps
      } : null,
      // Include market segment context
      marketSegment: {
        segment: marketSegment.segment,
        segmentLabel: marketSegment.segmentLabel,
        threatActorSophistication: marketSegment.modifiers?.threatActorSophistication,
        regulatoryScrutinyLevel: marketSegment.modifiers?.regulatoryScrutinyLevel
      },
      // Include organizational characteristics
      orgCharacteristics: orgCharacteristics.hasCharacteristics ? {
        selected: orgCharacteristics.selected,
        selectedLabels: orgCharacteristics.selectedLabels,
        hasBreachNotificationAdditions: orgCharacteristics.modifiers?.breachNotificationAdditions?.length > 0,
        hasRegulatoryOverlays: orgCharacteristics.modifiers?.regulatoryOverlays?.length > 0
      } : null
    }

    onSave('threatContext', contextData)
  }, [selectedProfile, activeSubIndustry, summaryScores, techEcosystem, marketSegment, orgCharacteristics, frameworkOverlays, onSave])

  // Mark initialization complete after first render cycle
  useEffect(() => {
    // Use a timeout to allow all initial state restoration to complete
    const timer = setTimeout(() => {
      isInitializing.current = false
    }, 500)
    return () => clearTimeout(timer)
  }, [])

  // Auto-save when values change (debounced to prevent rapid-fire saves)
  useEffect(() => {
    // Skip during initial load to prevent save cascade
    if (isInitializing.current) return
    if (!selectedProfile) return

    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    // Debounce save by 300ms to batch rapid changes
    saveTimeoutRef.current = setTimeout(() => {
      handleSave()
    }, 300)

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [selectedProfile, handleSave])

  if (!selectedProfile) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-4 text-gray-500 dark:text-gray-400">Loading threat profile...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Profile Recommendation Tile */}
      {!hideRecommendation && <div className="card p-6">
        <div className="flex items-start justify-between border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Recommended Threat Profile
            </h3>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              Based on your industry, data types, and regulatory requirements
            </p>
          </div>
          {!showProfileOverride && (
            <button
              onClick={() => setShowProfileOverride(true)}
              className="text-sm text-primary-600 dark:text-accent-300 hover:text-primary-700 dark:hover:text-accent-200 flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Edit
            </button>
          )}
        </div>

        {/* Recommendation Tile */}
        {selectedProfile && !showProfileOverride && (
          <div className={`p-5 rounded-xl border-2 ${
            profileRecommendation?.confidence === 'high'
              ? 'border-green-400 dark:border-green-600 bg-green-50 dark:bg-green-900/20'
              : profileRecommendation?.confidence === 'medium'
              ? 'border-yellow-400 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20'
              : 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
          }`}>
            <div className="flex items-center gap-3 mb-2">
              <h4 className="text-xl font-bold text-gray-900 dark:text-white">
                {activeSubIndustry?.displayName || selectedProfile.verticalName}
              </h4>
              <div className="relative group">
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium cursor-help ${
                  profileRecommendation?.confidence === 'high'
                    ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                    : profileRecommendation?.confidence === 'medium'
                    ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300'
                    : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                }`}>
                  {profileRecommendation?.confidence === 'high' ? 'High Confidence' :
                   profileRecommendation?.confidence === 'medium' ? 'Medium Confidence' :
                   'Low Confidence'}
                </span>
                {/* Tooltip with Score Breakdown */}
                {profileRecommendation?.breakdown && (
                  <div className="absolute left-0 top-full mt-2 z-50 hidden group-hover:block">
                    <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg p-3 shadow-lg min-w-[180px]">
                      <p className="font-medium mb-2">Score Breakdown</p>
                      <div className="space-y-1">
                        {profileRecommendation.breakdown.industry > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-300">Industry:</span>
                            <span>+{profileRecommendation.breakdown.industry}</span>
                          </div>
                        )}
                        {profileRecommendation.breakdown.dataTypes > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-300">Data Types:</span>
                            <span>+{profileRecommendation.breakdown.dataTypes}</span>
                          </div>
                        )}
                        {profileRecommendation.breakdown.frameworks > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-300">Frameworks:</span>
                            <span>+{profileRecommendation.breakdown.frameworks}</span>
                          </div>
                        )}
                        {profileRecommendation.breakdown.marketSegment > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-300">Market Segment:</span>
                            <span>+{profileRecommendation.breakdown.marketSegment}</span>
                          </div>
                        )}
                      </div>
                      <div className="border-t border-gray-600 mt-2 pt-2 flex justify-between font-medium">
                        <span>Total:</span>
                        <span>{profileRecommendation.score}/{profileRecommendation.maxPossible}</span>
                      </div>
                      {/* Arrow */}
                      <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45"></div>
                    </div>
                  </div>
                )}
              </div>
              {isManualOverride && (
                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                  Manual Override
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              {activeSubIndustry?.description || selectedProfile.description}
            </p>
            {activeSubIndustry?.frameworkPriority && (
              <div className="mt-3 space-y-2">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Regulatory Framework Alignment</p>
                <div className="flex flex-wrap gap-1.5">
                  {activeSubIndustry.frameworkPriority.required?.map(fw => (
                    <span key={fw} className="px-2 py-0.5 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full">
                      {fw.toUpperCase()} — Required
                    </span>
                  ))}
                  {activeSubIndustry.frameworkPriority.recommended?.map(fw => (
                    <span key={fw} className="px-2 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full">
                      {fw.toUpperCase()} — Recommended
                    </span>
                  ))}
                  {activeSubIndustry.frameworkPriority.optional?.map(fw => (
                    <span key={fw} className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
                      {fw.toUpperCase()} — Optional
                    </span>
                  ))}
                </div>
              </div>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
              {profileRecommendation?.explanation || 'Based on organizational profile'}
            </p>
          </div>
        )}

        {/* Profile Override Selector */}
        {showProfileOverride && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Select a different threat profile if the recommendation doesn't match your organization
              </p>
              <button
                onClick={() => setShowProfileOverride(false)}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                Cancel
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {activeProfiles.map(profile => (
                <button
                  key={profile.verticalKey}
                  onClick={() => handleProfileChange(profile.verticalKey)}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    selectedProfile?.verticalKey === profile.verticalKey
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {profile.verticalName}
                    </span>
                    {profile.verticalKey === profileRecommendation?.profileId && (
                      <span className="text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">
                        Recommended
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {profile.description}
                  </p>
                </button>
              ))}
            </div>

            {isManualOverride && (
              <div className="flex justify-end">
                <button
                  onClick={handleResetToRecommendation}
                  className="text-sm text-primary-600 dark:text-accent-300 hover:text-primary-700 dark:hover:text-accent-200"
                >
                  Reset to Recommended Profile
                </button>
              </div>
            )}
          </div>
        )}
      </div>}

      {!showOnlyRecommendation && <>
      {/* Technology Stack Context */}
      {techEcosystem && (
        <div className={`card p-6 ${
          techEcosystem.riskElevation === 'critical'
            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            : techEcosystem.riskElevation === 'elevated'
            ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
            : ''
        }`}>
          <div className="border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Technology Stack Context</h3>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Primary Ecosystem</p>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                techEcosystem.primary === 'microsoft' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' :
                techEcosystem.primary === 'google' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' :
                techEcosystem.primary === 'cloud-native' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300' :
                techEcosystem.primary === 'on-premises' ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' :
                'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}>
                {techEcosystem.primary === 'microsoft' ? 'Microsoft Ecosystem' :
                 techEcosystem.primary === 'google' ? 'Google Ecosystem' :
                 techEcosystem.primary === 'cloud-native' ? 'Cloud-Native' :
                 techEcosystem.primary === 'on-premises' ? 'On-Premises' :
                 'Mixed Environment'}
              </span>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Risk Assessment</p>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                techEcosystem.riskElevation === 'critical'
                  ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                  : techEcosystem.riskElevation === 'elevated'
                  ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                  : 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
              }`}>
                {techEcosystem.riskElevation === 'critical' ? 'Critical Gaps Detected' :
                 techEcosystem.riskElevation === 'elevated' ? 'Elevated Risk' :
                 'Standard Risk Profile'}
              </span>
            </div>
          </div>

          {/* Security Gaps Warning */}
          {(techEcosystem.gaps.noBackup || techEcosystem.gaps.noEndpoint || techEcosystem.gaps.noIdentity ||
            techEcosystem.gaps.traditionalAV || techEcosystem.gaps.manualBackup) && (
            <div className="mt-4 p-3 bg-white/50 dark:bg-black/20 rounded-lg">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                Identified Security Gaps:
              </p>
              <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                {techEcosystem.gaps.noBackup && (
                  <li className="flex items-center">
                    <svg className="w-4 h-4 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    No backup solution - ransomware recovery capability severely limited
                  </li>
                )}
                {techEcosystem.gaps.noEndpoint && (
                  <li className="flex items-center">
                    <svg className="w-4 h-4 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    No endpoint protection - malware detection capability missing
                  </li>
                )}
                {techEcosystem.gaps.noIdentity && (
                  <li className="flex items-center">
                    <svg className="w-4 h-4 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    No centralized identity management - access control weaknesses likely
                  </li>
                )}
                {techEcosystem.gaps.traditionalAV && (
                  <li className="flex items-center">
                    <svg className="w-4 h-4 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Traditional AV only - limited protection against modern threats
                  </li>
                )}
                {techEcosystem.gaps.manualBackup && (
                  <li className="flex items-center">
                    <svg className="w-4 h-4 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Manual backups - recovery time and reliability concerns
                  </li>
                )}
              </ul>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                These gaps will be factored into threat likelihood calculations and recommendations.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Market Segment & Organizational Context */}
      <div className="card p-6">
        <div className="border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Organizational Context</h3>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Market Segment */}
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Market Segment</p>
            <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                marketSegment.segment === 'upper-mid-market' ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300' :
                marketSegment.segment === 'mid-market' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' :
                'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
              }`}>
                {marketSegment.segmentLabel}
              </span>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                {marketSegment.description}
              </p>
              {marketSegment.derivedFrom?.effectiveRule && marketSegment.derivedFrom.effectiveRule !== 'Default (no data available)' && (
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  Derived from: {marketSegment.derivedFrom.effectiveRule}
                </p>
              )}
            </div>
          </div>

          {/* Threat Actor Sophistication */}
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Expected Threat Sophistication</p>
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                marketSegment.modifiers?.threatActorSophistication === 'targeted' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' :
                marketSegment.modifiers?.threatActorSophistication === 'targeted-opportunistic' ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300' :
                'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300'
              }`}>
                {marketSegment.modifiers?.threatActorSophistication === 'targeted' ? 'Sophisticated / Targeted' :
                 marketSegment.modifiers?.threatActorSophistication === 'targeted-opportunistic' ? 'Mixed / Semi-Targeted' :
                 'Opportunistic / Automated'}
              </span>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                {marketSegment.modifiers?.threatActorDescription}
              </p>
            </div>
          </div>
        </div>

        {/* Organizational Characteristics */}
        {orgCharacteristics.hasCharacteristics && (
          <div className="mt-6">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Organizational Characteristics</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {orgCharacteristics.selectedLabels.map((label, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300"
                >
                  {label}
                </span>
              ))}
            </div>

            {/* Regulatory Overlays from Characteristics */}
            {orgCharacteristics.modifiers?.regulatoryOverlays?.length > 0 && (
              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <p className="text-xs font-medium text-purple-700 dark:text-purple-300 mb-2">
                  Additional Regulatory Overlays
                </p>
                <div className="flex flex-wrap gap-1">
                  {orgCharacteristics.modifiers.regulatoryOverlays.map((overlay, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 text-xs rounded"
                    >
                      {overlay}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Narrative Frames */}
            {orgCharacteristics.modifiers?.narrativeFrames?.length > 0 && (
              <div className="mt-3 space-y-2">
                {orgCharacteristics.modifiers.narrativeFrames.map((frame, idx) => (
                  <p key={idx} className="text-xs text-gray-600 dark:text-gray-400 italic">
                    {frame}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Industry Threat Landscape */}
      {!hideThreatLandscape && <div className="card p-6">
        <div className="border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-5">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Industry Threat Landscape</h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Common threats facing <span className="font-medium text-gray-600 dark:text-gray-300">{activeSubIndustry?.displayName || selectedProfile.verticalName}</span> organizations, ranked by likelihood and prevalence
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Top Threat Actors */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
              <span className="w-2 h-2 rounded-full bg-red-500 mr-2"></span>
              Threat Actors
            </h4>
            <div className="space-y-3">
              {[...selectedProfile.threatActors]
                .sort((a, b) => b.likelihood - a.likelihood)
                .slice(0, 4)
                .map(actor => {
                  const pct = Math.round(actor.likelihood * 100)
                  const color = pct >= 70 ? 'red' : pct >= 40 ? 'amber' : 'green'
                  return (
                    <div key={actor.id} className="group">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{actor.name}</span>
                        <span className={`text-xs font-semibold ${
                          color === 'red' ? 'text-red-600 dark:text-red-400' :
                          color === 'amber' ? 'text-amber-600 dark:text-amber-400' :
                          'text-green-600 dark:text-green-400'
                        }`}>{pct}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            color === 'red' ? 'bg-red-500' :
                            color === 'amber' ? 'bg-amber-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 hidden group-hover:block">
                        {actor.description}
                      </p>
                    </div>
                  )
                })}
            </div>
          </div>

          {/* Top Attack Vectors */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
              <span className="w-2 h-2 rounded-full bg-orange-500 mr-2"></span>
              Attack Vectors
            </h4>
            <div className="space-y-3">
              {[...selectedProfile.attackVectors]
                .sort((a, b) => b.prevalence - a.prevalence)
                .slice(0, 4)
                .map(vector => {
                  const pct = Math.round(vector.prevalence * 100)
                  const color = pct >= 70 ? 'red' : pct >= 40 ? 'amber' : 'green'
                  return (
                    <div key={vector.id} className="group">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{vector.name}</span>
                        <span className={`text-xs font-semibold ${
                          color === 'red' ? 'text-red-600 dark:text-red-400' :
                          color === 'amber' ? 'text-amber-600 dark:text-amber-400' :
                          'text-green-600 dark:text-green-400'
                        }`}>{pct}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            color === 'red' ? 'bg-red-500' :
                            color === 'amber' ? 'bg-amber-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 hidden group-hover:block">
                        {vector.description}
                      </p>
                    </div>
                  )
                })}
            </div>
          </div>

          {/* Critical Target Assets */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
              <span className="w-2 h-2 rounded-full bg-purple-500 mr-2"></span>
              Target Assets
            </h4>
            <div className="space-y-3">
              {[...displayTargetAssets]
                .sort((a, b) => b.criticality - a.criticality)
                .slice(0, 4)
                .map(asset => {
                  const pct = Math.round(asset.criticality * 100)
                  const color = pct >= 70 ? 'red' : pct >= 40 ? 'amber' : 'green'
                  return (
                    <div key={asset.id} className="group">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{asset.name}</span>
                        <span className={`text-xs font-semibold ${
                          color === 'red' ? 'text-red-600 dark:text-red-400' :
                          color === 'amber' ? 'text-amber-600 dark:text-amber-400' :
                          'text-green-600 dark:text-green-400'
                        }`}>{pct}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            color === 'red' ? 'bg-red-500' :
                            color === 'amber' ? 'bg-amber-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 hidden group-hover:block">
                        {asset.description}
                      </p>
                    </div>
                  )
                })}
            </div>
          </div>
        </div>
      </div>}

      {/* Summary Scores */}
      {!hideThreatPreview && summaryScores && (
        <div className="card p-6">
          <div className="border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Threat Relevance Preview</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {summaryScores.threatActorScore}%
                </div>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Threat Actor Score</div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                Likelihood that specific threat actors (ransomware groups, nation-states, insiders) would target your organization.
              </p>
            </div>
            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {summaryScores.attackVectorScore}%
                </div>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Attack Vector Score</div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                Prevalence of attack methods (phishing, BEC, supply chain) commonly observed in your industry.
              </p>
            </div>
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {summaryScores.targetAssetScore}%
                </div>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Target Asset Score</div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                Criticality of assets (PII, financial data, IP) that attackers typically prioritize in your sector.
              </p>
            </div>
          </div>
        </div>
      )}
      </>}
    </div>
  )
}

export default ThreatContextStep
